/**
 * Groq is the only AI vendor (docs/SPEC.md §10, decision 56): Whisper for speech-to-text and an OpenAI-compatible chat
 * model for Hindi/Hinglish transliteration. Server-side fetches to it are allow-listed here (CLAUDE.md rule 10):
 * https://api.groq.com/openai/v1/… only, nothing else.
 */

export const GROQ_API_BASE = 'https://api.groq.com/openai/v1';
export const GROQ_TRANSCRIPTION_URL = `${GROQ_API_BASE}/audio/transcriptions`;
export const GROQ_CHAT_URL = `${GROQ_API_BASE}/chat/completions`;

/**
 * Groq platform limits for speech-to-text (owner correction 2026-10-16, Groq docs): a direct file upload takes up to
 * 25 MB on any tier; the paid (dev) tier takes files up to 100 MB through the `url` parameter (Groq fetches the file).
 * So: ≤ 25 MB → upload (free org first, then paid); 25–100 MB → paid org only, with the public URL of our own saved
 * R2 object (decision 79: never an Instagram link); > 100 MB → the job fails as `too_large` (refunded).
 * Live on staging 2026-10-21 Groq answered a 41.6 MB file given by URL with HTTP 400 `media_too_large` ("size limit:
 * 26214400"): for our orgs the URL route is capped at 25 MB too. Decision 80 makes it rare: transcription uses the
 * reel's audio track (`audio/<pk>.m4a`, ~0.5 MB a minute), which is always uploaded.
 */
export const GROQ_MAX_UPLOAD_BYTES = 25_000_000;
export const GROQ_MAX_URL_BYTES = 100_000_000;

/**
 * What the app, side panel and MCP say when a job failed as `too_large` (no vendor names in customer copy). Since
 * decision 80 transcription uses the reel's small audio track (far under 25 MB); a job fails as too large only when
 * that track isn't saved and the video is over what the provider takes (Groq answered `media_too_large` above 25 MB even
 * by URL, 2026-10-21). A fresh scan of the creator's Reels tab saves the audio track.
 */
export const TOO_LARGE_TO_TRANSCRIBE = "This reel's video is too big to transcribe and its audio track isn't saved yet: open the creator's Reels tab with the extension to save it, then transcribe again. Nothing was charged.";

/** What clients say when a job failed as `video_mismatch` or `length_disputed` (the saved video isn't this reel's length). */
export const VIDEO_LENGTH_MISMATCH = "The saved video doesn't match this reel's length, so it wasn't transcribed. Your credits were refunded.";
/**
 * No Groq key is set (neither GROQ_FREE_API_KEY nor GROQ_PAID_API_KEY): requests are refused with this message
 * (`transcription_unavailable`) before any credit is reserved, and jobs already queued fail with that code, refunded.
 */
export const TRANSCRIPTION_NOT_SET_UP = "Transcription isn't set up yet. An admin needs to add the Groq API key.";
/** What clients say when a job failed as `transcription_unavailable` (credits were refunded). */
export const TRANSCRIPTION_NOT_SET_UP_JOB = `${TRANSCRIPTION_NOT_SET_UP} Your credits were refunded.`;
/** Admin health detail when no Groq key is set. */
export const TRANSCRIPTION_NO_KEY_ADMIN = 'Transcription disabled: no Groq API key set (GROQ_FREE_API_KEY / GROQ_PAID_API_KEY)';
export const isLengthMismatch = (code: string | null | undefined) => code === 'video_mismatch' || code === 'length_disputed';

/**
 * Groq chat pricing used for spend accounting on the paid org (USD per million tokens). Verify against Groq's
 * pricing page before launch (docs/COSTS.md).
 */
export const GROQ_CHAT_USD_PER_M: Record<string, { input: number; output: number }> = {
  'llama-3.3-70b-versatile': { input: 0.59, output: 0.79 },
};

export function groqChatCostUsd(model: string, promptTokens: number, completionTokens: number): number {
  // Unknown model: priced like the default (a guard rail for the daily cap, not an invoice).
  const p = GROQ_CHAT_USD_PER_M[model] ?? GROQ_CHAT_USD_PER_M['llama-3.3-70b-versatile']!;
  const n = (v: number) => (Number.isFinite(v) && v > 0 ? v : 0);
  return (n(promptTokens) * p.input + n(completionTokens) * p.output) / 1_000_000;
}

/** Only https://api.groq.com/openai/v1/… (no credentials, no other host, port or path). */
export function isAllowedGroqUrl(url: string): boolean {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return false;
  }
  return u.protocol === 'https:' && !u.username && !u.password && u.origin === 'https://api.groq.com' && u.pathname.startsWith('/openai/v1/');
}
